import React, { useState, useRef, useEffect } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Loader2, ArrowLeft, Mail, Shield,
  Zap, Sun, Moon, CheckCircle, Clock,
} from 'lucide-react';

const FEATURES = [
  'GST-compliant and multi-currency invoices',
  'Auto-saved drafts, never lose work',
  'Full invoice history and PDF downloads',
  'One free invoice, no card required',
];

export default function AuthPage() {
  const { user, requestOtp, verifyOtp } = useAuth();
  const { isDark, toggle } = useTheme();
  const [searchParams] = useSearchParams();

  const [step, setStep]       = useState('email');
  const [email, setEmail]     = useState('');
  const [otp, setOtp]         = useState('');
  const [devOtp, setDevOtp]   = useState(null);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [slowRequest, setSlowRequest] = useState(false);

  const otpRef        = useRef(null);
  const countdownRef  = useRef(null);
  const slowTimerRef  = useRef(null);

  useEffect(() => {
    if (step === 'otp') {
      setTimeout(() => otpRef.current?.focus(), 60);
      setCountdown(60);
    }
  }, [step]);

  useEffect(() => {
    if (countdown <= 0) return;
    countdownRef.current = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(countdownRef.current);
  }, [countdown]);

  // Show warm-up notice if a request takes longer than 6 seconds — gives users
  // context instead of a silent spinner during a Render cold start.
  useEffect(() => {
    if (loading) {
      slowTimerRef.current = setTimeout(() => setSlowRequest(true), 6000);
    } else {
      clearTimeout(slowTimerRef.current);
      setSlowRequest(false);
    }
    return () => clearTimeout(slowTimerRef.current);
  }, [loading]);

  if (user) {
    if (searchParams.get('next') === 'upgrade') return <Navigate to="/?checkout=1" replace />;
    return <Navigate to="/" replace />;
  }

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await requestOtp(email);
      if (res?.dev_otp) setDevOtp(res.dev_otp);
      setStep('otp');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyOtp(email, otp);
    } catch (err) {
      setError(err.message);
      setOtp('');
      otpRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setOtp('');
    setDevOtp(null);
    setError('');
  };

  const handleResend = async () => {
    setError('');
    setLoading(true);
    setOtp('');
    setDevOtp(null);
    try {
      const res = await requestOtp(email);
      if (res?.dev_otp) setDevOtp(res.dev_otp);
      setCountdown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
      <SEO
        title="Sign In | Invilic"
        description="Sign in to Invilic to access your invoice history, email invoices to clients, and manage your account."
        canonical="https://www.invilic.com/auth"
        noindex={true}
      />

      {/* Top bar */}
      <header className="flex items-center justify-between px-6 py-3 flex-shrink-0">
        <Link to="/" className="flex items-center group">
          <img
            src="/2.png"
            alt="Invilic"
            className="h-14 w-auto transition-opacity group-hover:opacity-80"
          />
        </Link>

        <button
          onClick={toggle}
          className="p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Toggle theme"
        >
          {isDark ? <Sun size={16} /> : <Moon size={16} />}
        </button>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-5xl flex gap-16 items-center">

          {/* Left: Feature highlights (hidden on mobile) */}
          <div className="hidden lg:flex flex-col flex-1 gap-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-600/15 border border-blue-200 dark:border-blue-500/25 rounded-full text-xs text-blue-600 dark:text-blue-400 font-medium mb-6">
                <Zap className="w-3 h-3" />
                Free to start. No credit card.
              </div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white leading-snug mb-4">
                Professional invoices
                <br />
                <span className="text-blue-600 dark:text-blue-400">for every business</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                GST-compliant, multi-currency, and built for freelancers,
                consultants, and growing businesses in India and worldwide.
              </p>
            </div>

            <ul className="space-y-4">
              {FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-blue-50 dark:bg-blue-600/20 border border-blue-200 dark:border-blue-500/30 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Auth card */}
          <div className="w-full max-w-md flex-shrink-0 mx-auto lg:mx-0">

            {/* Ambient glow */}
            <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
              <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-80 h-80 bg-blue-600/8 rounded-full blur-3xl" />
            </div>

            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 shadow-lg dark:shadow-2xl dark:shadow-black/50">

              {/* Step 1: Email */}
              {step === 'email' && (
                <>
                  <div className="mb-8">
                    <div className="w-12 h-12 bg-blue-50 dark:bg-blue-600/20 border border-blue-200 dark:border-blue-500/30 rounded-xl flex items-center justify-center mb-5">
                      <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      Sign in or create account
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Enter your email. We'll send you a one-time code.
                      <span className="text-gray-500"> New accounts are created automatically.</span>
                    </p>
                  </div>

                  {error && (
                    <div className="mb-5 flex items-start gap-3 p-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/25 rounded-xl">
                      <span className="text-red-600 dark:text-red-400 text-sm">{error}</span>
                    </div>
                  )}

                  {loading && slowRequest && (
                    <div className="mb-5 flex items-start gap-3 p-3.5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/25 rounded-xl">
                      <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-amber-700 dark:text-amber-400 text-sm">
                        Server is waking up — this takes about 30s on the first visit. Hang tight!
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleEmailSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-2">
                        Email address
                      </label>
                      <input
                        type="email"
                        required
                        autoFocus
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading || !email}
                      className="w-full py-3 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-blue-600/25"
                    >
                      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {loading ? 'Sending code...' : 'Continue with email'}
                    </button>
                  </form>

                  <div className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-800 flex items-start gap-2.5">
                    <Shield className="w-4 h-4 text-gray-400 dark:text-gray-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-500 dark:text-gray-600 leading-relaxed">
                      Secure magic-link login. No passwords. Your first invoice is free
                      with no credit card required.
                    </p>
                  </div>
                </>
              )}

              {/* Step 2: OTP */}
              {step === 'otp' && (
                <>
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white mb-7 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>

                  <div className="mb-8">
                    <div className="w-12 h-12 bg-blue-50 dark:bg-blue-600/20 border border-blue-200 dark:border-blue-500/30 rounded-xl flex items-center justify-center mb-5">
                      <Zap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      Check your email
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      We sent a 6-character code to{' '}
                      <span className="font-semibold text-gray-800 dark:text-gray-200">{email}</span>
                    </p>
                  </div>

                  {/* Dev OTP hint */}
                  {devOtp && (
                    <div className="mb-5 p-3.5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/25 rounded-xl text-sm text-amber-700 dark:text-amber-400">
                      <span className="font-semibold">Dev mode:</span> code is{' '}
                      <button
                        type="button"
                        onClick={() => setOtp(devOtp)}
                        className="font-mono font-bold tracking-widest underline decoration-dotted hover:text-amber-600 dark:hover:text-amber-300 transition-colors"
                      >
                        {devOtp}
                      </button>{' '}
                      (click to fill)
                    </div>
                  )}

                  {error && (
                    <div className="mb-5 p-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/25 rounded-xl text-sm text-red-600 dark:text-red-400">
                      {error}
                    </div>
                  )}

                  {loading && slowRequest && (
                    <div className="mb-5 flex items-start gap-3 p-3.5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/25 rounded-xl">
                      <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-amber-700 dark:text-amber-400 text-sm">
                        Server is waking up — this takes about 30s on the first visit. Hang tight!
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleOtpSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        One-time code
                      </label>
                      <input
                        ref={otpRef}
                        type="text"
                        required
                        value={otp}
                        onChange={e => setOtp(e.target.value.toUpperCase())}
                        placeholder="A1B2C3"
                        maxLength={6}
                        autoComplete="one-time-code"
                        className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 font-mono text-2xl tracking-[0.35em] text-center focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading || otp.length < 6}
                      className="w-full py-3 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-blue-600/25"
                    >
                      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {loading ? 'Verifying...' : 'Verify and sign in'}
                    </button>
                  </form>

                  <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-600">
                    Didn't receive it?{' '}
                    {countdown > 0 ? (
                      <span className="text-gray-400 dark:text-gray-700">Resend in {countdown}s</span>
                    ) : (
                      <button
                        onClick={handleResend}
                        disabled={loading}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium disabled:opacity-50 transition-colors"
                      >
                        Resend code
                      </button>
                    )}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
